import { useState } from 'react'
import { tokens } from 'styling/tokens'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { XIcon } from 'assets/icons/XIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { ChipsContainer } from 'components/CheckBox/CheckBox.styled'
import { Alert, AlertType } from 'components/Alert/Alert'
import useModalClose from 'hooks/useModalClose'
import { Button, CursorPointer, Flex, ColorVariant } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Card, CardBody } from 'components/Card/Card'
import { IKitchenDish, IKitchenNewDishForm } from 'types/kitchen/kitchenDish.interface'
import { IKitchenIngredient } from 'types/kitchen/kitchenIngredient.interface'
import { KITCHEN_DIETARY_TAGS, KITCHEN_SUPPLIERS } from '../kitchenData'
import useFocusFirstInput from 'utils/useFocusFirstInput'

interface IProps {
  ingredients: IKitchenIngredient[]
  dishes: IKitchenDish[]
  closeModal: () => void
  onSave: (dish: IKitchenDish, active: boolean) => void
}

const emptyForm: IKitchenNewDishForm = {
  name: '',
  type: 'Prepared',
  tags: [],
  norm: [],
  supplier: '',
}

export const NewDishModal = ({ ingredients, dishes, closeModal, onSave }: IProps) => {
  const [form, setForm] = useState<IKitchenNewDishForm>(emptyForm)
  useModalClose(closeModal)
  useFocusFirstInput()

  const ing = (id: number) => ingredients.find((i) => i.id === id)

  const toggleTag = (t: string) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t],
    }))
  }

  const normValid = form.norm.length > 0 && form.norm.every((l) => l.ing && Number(l.qty) > 0)
  const gatePass = form.name.trim() && form.tags.length > 0 && (form.type === 'Prepared' ? normValid : !!form.supplier)
  const draftPass = form.name.trim() && form.tags.length > 0

  const liveCost = form.norm
    .filter((l) => l.ing && l.qty)
    .reduce((s, l) => {
      const i = ing(Number(l.ing))
      if (!i) return s
      return s + (Number(l.qty) / i.factor) * i.price
    }, 0)

  const buildDish = (active: boolean): IKitchenDish => ({
    id: Math.max(...dishes.map((x) => x.id)) + 1,
    name: form.name.trim(),
    type: form.type,
    tags: form.tags,
    active,
    norm:
      form.type === 'Prepared'
        ? form.norm.map((l) => ({ ing: Number(l.ing), qty: Number(l.qty) }))
        : [],
    supplier: form.type === 'Purchased' ? form.supplier : undefined,
  })

  const ingredientOptions = ingredients.map((i) => ({ label: i.name, value: i.id }))
  const supplierOptions = KITCHEN_SUPPLIERS.map((s) => ({ label: s, value: s }))

  return (
    <Modal black width='640px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>New Dish</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Input
          labelText='Dish name'
          placeholder='e.g. Groundnut Soup & Rice Balls'
          value={form.name}
          onChange={(e: any) => setForm({ ...form, name: e.target.value })}
          nomarg
        />
        <OtherText grey>Source type</OtherText>
        <Flex gap='0.625rem' width='100%'>
          {(
            [
              ['Prepared', 'Cooked on site — recipe (normative) required for activation'],
              ['Purchased', 'Bought finished — supplier required; deduction is the dish-SKU itself'],
            ] as const
          ).map(([t, d]) => (
            <Card key={t} onClick={() => setForm({ ...form, type: t, norm: t === 'Prepared' ? form.norm : [], supplier: t === 'Purchased' ? form.supplier : '' })} hoverBorder fullBorder={form.type === t ? tokens.accent.base : undefined} noScale>
              <CardBody>
                <BodyText weight='700'>{t}</BodyText>
                <OtherText grey>{d}</OtherText>
              </CardBody>
            </Card>
          ))}
        </Flex>
        <OtherText grey>Dietary tags (shared taxonomy — a dish may only serve tag sets it covers)</OtherText>
        <ChipsContainer>
          {KITCHEN_DIETARY_TAGS.map((t) => (
            <CheckChip key={t} text={t} active={form.tags.includes(t)} onClick={() => toggleTag(t)} />
          ))}
        </ChipsContainer>
        {form.type === 'Prepared' ? (
          <>
            <OtherText grey>Recipe (normative v1) — quantity per portion</OtherText>
            {form.norm.map((l, i) => {
              const sel = l.ing ? ing(Number(l.ing)) : null
              return (
                <Flex key={i} gap='0.5rem' center width='100%'>
                  <HonestSelectInput
                    width='100%'
                    labelText='Ingredient'
                    name={`ingredient-${i}`}
                    placeholder='— ingredient (SKU) —'
                    options={ingredientOptions}
                    defaultValue={l.ing ? Number(l.ing) : undefined}
                    onChange={(v: any) =>
                      setForm({
                        ...form,
                        norm: form.norm.map((x, j) => (j === i ? { ...x, ing: v?.value || '' } : x)),
                      })
                    }
                    nomarg
                  />
                  <Input
                    type='number'
                    width='6rem'
                    placeholder='Qty'
                    value={String(l.qty)}
                    onChange={(e: any) =>
                      setForm({
                        ...form,
                        norm: form.norm.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)),
                      })
                    }
                    nomarg
                  />
                  <OtherText grey>{sel ? sel.recipeUnit : '—'}</OtherText>
                  <OtherText grey style={{ width: '3.75rem', textAlign: 'right' }}>
                    {sel && l.qty ? `$${((Number(l.qty) / sel.factor) * sel.price).toFixed(3)}` : ''}
                  </OtherText>
                  <CursorPointer onClick={() => setForm({ ...form, norm: form.norm.filter((_, j) => j !== i) })}>
                    <XIcon />
                  </CursorPointer>
                </Flex>
              )
            })}
            <Button orangeBorder onClick={() => setForm({ ...form, norm: [...form.norm, { ing: '', qty: '' }] })}>
              Add ingredient
            </Button>
            {normValid && (
              <Flex gap='0.5rem' center>
                <OtherText grey>Cost per portion (live, batch-weighted from ledger prices):</OtherText>
                <BodyText weight='700' style={{ color: tokens.accent.base, fontSize: '1rem' }}>
                  ${liveCost.toFixed(2)}
                </BodyText>
              </Flex>
            )}
          </>
        ) : (
          <>
            <HonestSelectInput
              width='100%'
              labelText='Supplier (mandatory for purchased dishes)'
              name='supplier'
              placeholder='— select supplier —'
              options={supplierOptions}
              defaultValue={form.supplier || undefined}
              onChange={(v: any) => setForm({ ...form, supplier: v?.value || '' })}
            />
            <OtherText grey>
              On activation a dish-SKU is auto-created in the central Inventory ledger so receipts and consumption of this item are tracked like any other stock.
            </OtherText>
          </>
        )}
        {!gatePass && draftPass && (
          <Alert
            title='Activation gate not met'
            description={
              form.type === 'Prepared'
                ? 'Recipe needs at least one complete ingredient line. You can still save as draft.'
                : 'Supplier must be selected. You can still save as draft.'
            }
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        )}
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button onClick={closeModal}>Cancel</Button>
          <Button orangeBorder disabled={!draftPass} onClick={() => onSave(buildDish(false), false)}>
            Save as Draft
          </Button>
          <Button orange disabled={!gatePass} onClick={() => onSave(buildDish(true), true)}>
            Create & Activate
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
